import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { Dimensions } from "react-native";
import { useFragment } from "react-relay";

import { isActivePatronRequestSeries } from "@/features/BookingParts/components/BookingInfo/isActivePatronRequestSeries";
import {
  Button,
  Radio,
  RadioGroup,
} from "@whoosh/design-system/src/components/controls";
import { LoadingSpinner } from "@whoosh/design-system/src/components/display/Loader/LoadingSpinner";
import { TrashIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { ClubStackNavigatorParamList } from "../../../../../../../navigators/ClubNavigator";
import { CancelBookingModal } from "../../../CancelBooking/CancelBookingModal";
import { useDeleteSlotBooking } from "../../hooks/useDeleteSlotBooking";
import { useShouldPresentBookingAsLockedRequest } from "../BookingRequest/components/AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import CancelBookingModalConfirmation from "./CancelBookingModalConfirmation/CancelBookingModalConfirmation";
import { CancelBookingModification } from "./CancelBookingModification";
import { CancelBookingButton_Booking$key } from "./__generated__/CancelBookingButton_Booking.graphql";

type Props = {
  bookingRef: CancelBookingButton_Booking$key | null;
  currentSessionUserClientId: string;
};

export const CancelBookingButton = ({
  bookingRef,
  currentSessionUserClientId,
}: Props) => {
  const [isCancelBookingModalOpen, setIsCancelBookingModalOpen] =
    useState(false);
  const [
    isCancelBookingModalConfirmationOpen,
    setIsCancelBookingModalConfirmationOpen,
  ] = useState(false);
  const [isCancelScopeModalOpen, setIsCancelScopeModalOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState<
    "THIS_ONLY" | "THIS_AND_FOLLOWING"
  >("THIS_ONLY");
  const [deleteSlotBooking, isDeletingSlotBooking] = useDeleteSlotBooking();
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const booking = useFragment(cancelBookingButtonFragment, bookingRef);
  const isPublicClub =
    booking?.startSlot?.agenda?.facility?.club?.supportsPublic;

  const refundableStatus = booking?.refundableStatus;

  const isCurrentUserOwnerOfBooking =
    !!booking?.ownerClient?.id &&
    !!currentSessionUserClientId &&
    booking?.ownerClient?.id === currentSessionUserClientId;

  const shouldShowCancelBookingButton =
    isCurrentUserOwnerOfBooking &&
    !booking?.startSlot?.isInCancelWindow &&
    refundableStatus !== "NOT_REFUNDABLE";

  const canTransferOwnership =
    booking?.startSlot?.agenda?.facility?.privateBookingPermissionSet
      ?.canTransferBookingOwnership ?? false;

  const canRemoveFromBooking =
    isCurrentUserOwnerOfBooking &&
    booking?.rounds?.count &&
    booking?.rounds?.count > 1 &&
    canTransferOwnership;

  const successCallback = () => {
    setIsCancelBookingModalOpen(false);
    setIsCancelScopeModalOpen(false);
    navigation.navigate("Home", { screen: "MainHome" });
  };

  const deleteBooking = (
    seriesScope: "THIS_ONLY" | "THIS_AND_FOLLOWING" = "THIS_ONLY"
  ) => {
    if (isPublicClub) {
      setIsCancelBookingModalConfirmationOpen(true);
    } else if (canRemoveFromBooking && !isRequestPresentation) {
      setIsCancelBookingModalOpen(true);
    } else {
      deleteSlotBooking(
        {
          startSlotId: booking?.startSlot?.id ?? "",
          bookingId: booking?.id ?? "",
          notifyMember: true,
          issueRefund:
            !isPatronRequestSeriesBooking && refundableStatus === "REFUNDABLE",
          seriesScope,
        },
        successCallback
      );
    }
  };

  const isQueueSlot = booking?.startSlot?.__typename === "QueueSlot";
  const shouldPresentAsRequest = useShouldPresentBookingAsLockedRequest(
    booking ?? null
  );
  const isRequestPresentation = isQueueSlot || shouldPresentAsRequest;
  // A fulfilled occurrence is still presented as a locked request and keeps
  // bookingSeries.id, but its start slot is a real TimeSlot, not a QueueSlot --
  // delete_patron_request_series_from/3 requires a queue-backed source, so
  // "This and following" must only be offered while isQueueSlot is true. A
  // queue-backed booking can also belong to a generic staff-created recurring
  // series (kind: null) rather than a patron request one -- ensure_patron_request_series/1
  // rejects "This and following" for those, so gate on the series' kind too.
  const isPatronRequestSeriesBooking =
    isQueueSlot && booking?.bookingSeries?.kind === "PATRON_REQUEST";
  const isRecurringRequest =
    isPatronRequestSeriesBooking &&
    isActivePatronRequestSeries({
      hasSeriesRecurrence: booking?.bookingSeries?.kind === "PATRON_REQUEST",
      remainingBookingCount:
        booking?.__typename === "StandardBooking" ||
        booking?.__typename === "LessonBooking"
          ? booking.seriesCount ?? 0
          : 0,
    });

  const closeCancelScopeModal = () => {
    setIsCancelScopeModalOpen(false);
    setCancelScope("THIS_ONLY");
  };

  const isConfirmedBooking = booking?.status === "CONFIRMED";

  const windowWidth = Dimensions.get("window").width;

  if (!booking || !isConfirmedBooking) return null;

  return shouldShowCancelBookingButton ? (
    <VStack width="100%">
      <Button
        accessibilityLabel={t`Cancel Booking button`}
        width="100%"
        size="small"
        variant="danger"
        onPress={() => {
          if (isRecurringRequest) {
            setIsCancelScopeModalOpen(true);
          } else {
            deleteBooking();
          }
        }}
        testID="booking-details-cancel-btn"
        leftIcon={
          isDeletingSlotBooking ? undefined : (
            <TrashIcon color="icon-status-error" />
          )
        }
      >
        {isDeletingSlotBooking ? (
          <LoadingSpinner />
        ) : isRequestPresentation ? (
          t`Cancel Request`
        ) : (
          t`Cancel Booking`
        )}
      </Button>
      {!isPublicClub ? (
        <CancelBookingModal
          isOpen={isCancelBookingModalOpen}
          onClose={() => setIsCancelBookingModalOpen(false)}
          bookingRef={booking}
          currentSessionUserClientId={currentSessionUserClientId}
        />
      ) : null}
      {isPublicClub ? (
        <CancelBookingModalConfirmation
          isOpen={isCancelBookingModalConfirmationOpen}
          onClose={() => setIsCancelBookingModalConfirmationOpen(false)}
          onConfirmation={() => {
            setIsCancelBookingModalConfirmationOpen(false);
            deleteSlotBooking(
              {
                startSlotId: booking?.startSlot?.id ?? "",
                bookingId: booking?.id ?? "",
                notifyMember: true,
                issueRefund:
                  !isPatronRequestSeriesBooking &&
                  refundableStatus === "REFUNDABLE",
              },
              successCallback
            );
          }}
        />
      ) : null}
      <StandardModal
        isOpen={isCancelScopeModalOpen}
        onClose={closeCancelScopeModal}
        upperTitle={t`Cancel recurring request`}
        content={
          <VStack paddingHorizontal={6} paddingVertical={4} gap={4}>
            <Text variant="mainBaseMedium" color="text-secondary">
              {t`Choose whether to cancel only this request or this request and every following request in the series.`}
            </Text>
            <RadioGroup
              value={cancelScope}
              onChange={(value) =>
                setCancelScope(
                  value === "THIS_AND_FOLLOWING"
                    ? "THIS_AND_FOLLOWING"
                    : "THIS_ONLY"
                )
              }
              accessibilityLabel={t`Choose which recurring requests to cancel`}
            >
              <Box paddingBottom={1}>
                <Radio
                  value="THIS_ONLY"
                  label={t`This request only`}
                  accessibilityLabel={t`Cancel this request only`}
                  testID="cancel-recurring-request-this-only-radio"
                />
              </Box>
              <Radio
                value="THIS_AND_FOLLOWING"
                label={t`This and following requests`}
                accessibilityLabel={t`Cancel this and following requests`}
                testID="cancel-recurring-request-this-and-following-radio"
              />
            </RadioGroup>
          </VStack>
        }
        footerContent={
          <HStack gap={4} justifyContent="flex-end" paddingBottom={6}>
            <Button
              variant="white"
              size="small"
              accessibilityLabel={t`Keep recurring requests`}
              onPress={closeCancelScopeModal}
            >
              {t`Keep Requests`}
            </Button>
            <Button
              variant="danger"
              size="small"
              accessibilityLabel={t`Confirm recurring request cancellation`}
              isLoading={isDeletingSlotBooking}
              onPress={() => deleteBooking(cancelScope)}
            >
              {cancelScope === "THIS_AND_FOLLOWING"
                ? t`Cancel This and Following`
                : t`Cancel This Request`}
            </Button>
          </HStack>
        }
      />
    </VStack>
  ) : booking?.startSlot?.isInCancelWindow ? (
    <Box width="100%" maxWidth={windowWidth - 40}>
      <CancelBookingModification />
    </Box>
  ) : null;
};

const cancelBookingButtonFragment = graphql`
  fragment CancelBookingButton_Booking on Booking {
    id
    __typename
    bookingSeries {
      id
      kind
    }
    ...useShouldPresentBookingAsLockedRequest_booking
    ...CancelBookingModal_Booking
    rounds {
      count
    }
    ... on StandardBooking {
      seriesCount
      status
      refundableStatus
    }
    ... on LessonBooking {
      seriesCount
      status
      refundableStatus
    }
    startSlot {
      id
      __typename
      ... on TimeSlot {
        isInCancelWindow
      }
      agenda {
        facility {
          privateBookingPermissionSet {
            canTransferBookingOwnership
          }
          club {
            supportsPublic
          }
        }
      }
    }
    ownerClient {
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
  }
`;
